/**
 * CapabilityCriteriaChecklist
 *
 * Displays per-level binary criteria for a capability based on the selected
 * maturity level. Shows criteria for levels 1..selectedLevel so the assessor
 * can see what must be true at each level.
 *
 * Each criterion entry is either:
 *   ✓ met     — level.level <= selectedLevel
 *   ○ unmet   — level.level > selectedLevel  (never shown — we only display 1..N)
 *
 * A criterion for a level that IS within 1..selectedLevel may still be flagged
 * as "not yet confirmed" by the assessor via the unmetLevelIds set. Unmet items
 * trigger the required-comment warning.
 */

import type { MaturityLevel } from '../../graphql/types';
import '@tylertech/forge/icon';
import './CapabilityCriteriaChecklist.css';

export interface CapabilityCriteriaChecklistProps {
  /** All 6 levels defined for this capability. */
  levels: MaturityLevel[];
  /** Currently selected maturity level (1-6). Criteria for 1..selectedLevel shown. */
  selectedLevel: number;
  /**
   * Set of level numbers (within 1..selectedLevel) that the assessor has
   * explicitly marked as not yet fully met. Shown with a warning icon.
   */
  unmetLevelIds: Set<number>;
  /**
   * Called when the assessor toggles the met/unmet state of a criterion level.
   */
  onToggleUnmet: (levelNumber: number) => void;
  /** Whether the checklist is collapsed (all criteria met and level confirmed). */
  collapsed: boolean;
}

/**
 * Derive the display criterion text for a level. If the level has explicit
 * binary `criteria` items, join them. Otherwise fall back to the description.
 */
function getLevelCriterionText(level: MaturityLevel): string {
  if (level.criteria && level.criteria.length > 0) {
    return level.criteria.join('; ');
  }
  return level.description;
}

export function CapabilityCriteriaChecklist({
  levels,
  selectedLevel,
  unmetLevelIds,
  onToggleUnmet,
  collapsed,
}: CapabilityCriteriaChecklistProps): JSX.Element | null {
  // Only show criteria for levels 1..selectedLevel
  const visibleLevels = levels.filter((l) => l.level <= selectedLevel);

  if (visibleLevels.length === 0) {
    return null;
  }

  const ForgeIcon = 'forge-icon' as React.ElementType;

  return (
    <div
      className={`criteria-checklist${collapsed ? ' criteria-checklist--collapsed' : ''}`}
      aria-label="Level criteria checklist"
    >
      {!collapsed && (
        <ul className="criteria-checklist__list" role="list">
          {visibleLevels.map((levelDef) => {
            const isUnmet = unmetLevelIds.has(levelDef.level);
            const itemClass = isUnmet
              ? 'criteria-checklist__item criteria-checklist__item--unmet'
              : 'criteria-checklist__item criteria-checklist__item--met';

            return (
              <li key={levelDef.level} className={itemClass}>
                <button
                  type="button"
                  className="criteria-checklist__toggle"
                  aria-pressed={isUnmet}
                  aria-label={`Level ${levelDef.level} ${levelDef.name}: ${isUnmet ? 'not yet met — click to mark met' : 'met — click to mark unmet'}`}
                  onClick={() => onToggleUnmet(levelDef.level)}
                >
                  <ForgeIcon
                    name={isUnmet ? 'warning' : 'check_circle'}
                    className="criteria-checklist__icon"
                    aria-hidden="true"
                  />
                </button>
                <span className="criteria-checklist__text">
                  <span className="criteria-checklist__level-badge">
                    L{levelDef.level} {levelDef.name}
                  </span>
                  {getLevelCriterionText(levelDef)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {collapsed && (
        <p className="criteria-checklist__all-met" aria-live="polite">
          <ForgeIcon
            name="check_circle"
            className="criteria-checklist__icon--all-met"
            aria-hidden="true"
          />
          All criteria met
        </p>
      )}
    </div>
  );
}
